/**
 * useWorkerWS
 *
 * Cloudflare Worker WebSocketへの接続を管理するフック。
 * - 自動再接続（指数バックオフ）
 * - メッセージ受信コールバック
 * - 送信キュー（接続前に積まれたメッセージを接続後に送信）
 */

import { useRef, useCallback, useEffect } from 'react';
import type { ServerMessage, TranslationDirection } from '../types';

interface UseWorkerWSOptions {
  url: string;
  role: 'operator' | 'caption';
  token?: string;
  sessionId?: string;
  onMessage: (msg: ServerMessage) => void;
  onOpen?: () => void;
  onClose?: () => void;
  autoConnect?: boolean;
}

interface UseWorkerWSReturn {
  connect: () => void;
  disconnect: () => void;
  send: (data: object) => boolean;
  sendAudio: (base64: string) => void;
  sendDirection: (dir: TranslationDirection) => void;
  sendStart: () => void;
  sendStop: () => void;
  sendPause: () => void;
  sendResume: () => void;
  sendClear: () => void;
  sendGlossary: (terms: string[]) => void;
  sendPosition: (pos: 'bottom' | 'top' | 'center') => void;
  sendFontSize: (size: number) => void;
  sendObsStyle: (style: string) => void;
  sendShowOriginal: (v: boolean) => void;
  isConnected: () => boolean;
}

const MAX_RECONNECT_ATTEMPTS = 10;
const BASE_RECONNECT_DELAY = 1500;

export function useWorkerWS({
  url,
  role,
  token,
  sessionId = 'default',
  onMessage,
  onOpen,
  onClose,
  autoConnect = false,
}: UseWorkerWSOptions): UseWorkerWSReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldReconnectRef = useRef(false);
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);

  // コールバック参照を最新に保つ
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  useEffect(() => { onOpenRef.current = onOpen; }, [onOpen]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const buildURL = useCallback(() => {
    const params = new URLSearchParams({
      role,
      session: sessionId,
    });
    if (token) params.set('token', token);
    // イベント固定の字幕URL（session=event-<公開ID>）は、サーバー側で今のセッションに振り分ける
    if (sessionId.startsWith('event-')) params.set('event', sessionId.slice(6));

    // ws: / wss: 変換
    const wsUrl = url.replace(/^http/, 'ws');
    return `${wsUrl}/ws?${params}`;
  }, [url, role, token, sessionId]);

  // 接続中（つなぎ直しの途中）に送った操作は、つながった直後にまとめて送る。
  // 以前は捨てていたため、購入直後の初回「開始」が届かないことがあった。音声は溜めない。
  const pendingRef = useRef<string[]>([]);
  const send = useCallback((data: object) => {
    const payload = JSON.stringify(data);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(payload);
      return true;
    }
    const type = (data as { type?: string }).type;
    if (wsRef.current?.readyState === WebSocket.CONNECTING && type !== 'audio') {
      if (pendingRef.current.length < 50) pendingRef.current.push(payload);
      return true;
    }
    return false;
  }, []);

  const connect = useCallback(() => {
    // 既存接続をクリーンアップ
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      if (wsRef.current.readyState < WebSocket.CLOSING) {
        wsRef.current.close(1000);
      }
      wsRef.current = null;
    }

    shouldReconnectRef.current = true;
    pendingRef.current = [];

    try {
      const ws = new WebSocket(buildURL());
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        const queued = pendingRef.current;
        pendingRef.current = [];
        for (const m of queued) { try { ws.send(m); } catch { /* ignore */ } }
        onOpenRef.current?.();
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const msg = JSON.parse(event.data as string) as ServerMessage;
          onMessageRef.current(msg);
        } catch {
          // JSON parse error - ignore
        }
      };

      ws.onclose = (event: CloseEvent) => {
        wsRef.current = null;
        onCloseRef.current?.();

        // コード 1000（正常終了）以外、かつshouldReconnect=trueなら再接続
        if (
          shouldReconnectRef.current &&
          event.code !== 1000 &&
          reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS
        ) {
          reconnectAttemptsRef.current++;
          const delay = Math.min(
            BASE_RECONNECT_DELAY * Math.pow(1.5, reconnectAttemptsRef.current - 1),
            30000
          );
          reconnectTimerRef.current = setTimeout(() => {
            if (shouldReconnectRef.current) {
              connect();
            }
          }, delay);
        }
      };

      ws.onerror = () => {
        // エラーはoncloseでも処理される
      };
    } catch (err) {
      console.error('WebSocket接続エラー:', err);
    }
  }, [buildURL]);

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
  }, []);

  const isConnected = useCallback(() => {
    return wsRef.current?.readyState === WebSocket.OPEN;
  }, []);

  // ─── 送信メソッド ───────────────────
  const sendAudio = useCallback(
    (base64: string) => send({ type: 'audio', data: base64 }),
    [send]
  );

  const sendDirection = useCallback(
    (dir: TranslationDirection) => send({ type: 'direction', value: dir }),
    [send]
  );

  const sendStart = useCallback(() => send({ type: 'start' }), [send]);
  const sendStop = useCallback(() => send({ type: 'stop' }), [send]);
  const sendPause = useCallback(() => send({ type: 'pause' }), [send]);
  const sendResume = useCallback(() => send({ type: 'resume' }), [send]);
  const sendClear = useCallback(() => send({ type: 'clear' }), [send]);

  const sendPosition = useCallback(
    (pos: 'bottom' | 'top' | 'center') => send({ type: 'position', value: pos }),
    [send]
  );

  const sendFontSize = useCallback(
    (size: number) => send({ type: 'fontSize', value: size }),
    [send]
  );

  const sendObsStyle = useCallback(
    (style: string) => send({ type: 'obsStyle', value: style }),
    [send]
  );

  const sendShowOriginal = useCallback(
    (v: boolean) => send({ type: 'showOriginal', value: v }),
    [send]
  );

  const sendGlossary = useCallback(
    (terms: string[]) => send({ type: 'glossary', terms }),
    [send]
  );

  // autoConnect
  useEffect(() => {
    if (autoConnect) {
      connect();
    }
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    connect,
    disconnect,
    send,
    sendAudio,
    sendDirection,
    sendStart,
    sendStop,
    sendPause,
    sendResume,
    sendClear,
    sendGlossary,
    sendPosition,
    sendFontSize,
    sendObsStyle,
    sendShowOriginal,
    isConnected,
  };
}
